// filepath: /Users/shaunscott/Dev Projects/shaunscott_com/src/components/error/ErrorBoundary.tsx
import React from 'react';

/**
 * Props for ErrorBoundary component
 */
interface ErrorBoundaryProps {
  /** Child components to wrap */
  children: React.ReactNode;
  /** Whether to show reset button */
  showReset?: boolean;
  /** Callback when reset is clicked */
  onReset?: () => void;
}

/**
 * State for ErrorBoundary component
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Error Boundary component to catch JavaScript errors in children components
 * and display a fallback UI instead of crashing the whole app
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log the error to an error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ errorInfo });
    
    // Track error in analytics if available
    if (window.gtag) {
      window.gtag('event', 'error', {
        'event_category': 'Error Boundary',
        'event_label': error.toString(),
        'value': errorInfo?.componentStack || 'Unknown stack'
      });
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className="error-boundary-container p-8 max-w-md mx-auto my-8 bg-red-50 border border-red-200 rounded-lg text-center">
          <h2 className="text-xl font-bold text-red-800 mb-4">Something went wrong</h2>
          <p className="mb-4 text-red-700">We apologize for the inconvenience. Please try refreshing the page.</p>
          {this.props.showReset && (
            <button
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              onClick={() => {
                this.setState({ hasError: false, error: null, errorInfo: null });
                if (typeof this.props.onReset === 'function') {
                  this.props.onReset();
                }
              }}
            >
              Try Again
            </button>
          )}
          {import.meta.env.DEV && this.state.error && (
            <div className="mt-4 p-4 bg-gray-800 text-white rounded overflow-auto text-left">
              <p className="font-mono text-sm">{this.state.error.toString()}</p>
              {this.state.errorInfo && (
                <details className="mt-2">
                  <summary className="cursor-pointer">Component Stack</summary>
                  <pre className="mt-2 text-xs overflow-auto">{this.state.errorInfo.componentStack}</pre>
                </details>
              )}
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

